// Session/SpecialSessionAnswersTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Table, Text, ActionIcon, Group, Modal, Stack, Button, Center, Loader, Alert, Checkbox, Card,
} from '@mantine/core';
import { IconEdit, IconTrash, IconAlertCircle, IconPhoto, IconClipboardOff } from '@tabler/icons-react';
import {
  useRegistrationService,
  RegistrationCustomSessionDataModelView,
} from '@actschurch/ccms-sdk';
import { DynamicField } from '@/components/advanced/FormBuilder';
import type { BuilderField, FieldOption } from '@/components/advanced/FormBuilder/types/BuilderField';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';
import { EmptyState } from '../Components/EmptyState';

export interface SpecialSessionAnswersTabProps {
  registrationSessionID: number;
  onAnswersCleared?: () => void; // Triggered when answers are cleared/deleted
  /** The registration's members, to show a 'member' answer by name rather than memberID. */
  memberOptions?: FieldOption[];
}

function formatValue(row: RegistrationCustomSessionDataModelView): string {
  if (row.valueBoolean !== null && row.valueBoolean !== undefined) return row.valueBoolean ? 'Yes' : 'No';
  if (row.valueDecimal !== null && row.valueDecimal !== undefined) return String(row.valueDecimal);
  if (row.valueNumber !== null && row.valueNumber !== undefined) return String(row.valueNumber);
  if (row.valueDateTime !== null && row.valueDateTime !== undefined) return String(row.valueDateTime);
  return row.valueText ?? row.value ?? '—';
}

/** 'member' answers store a memberID; show the member's name when we have it. */
function formatAnswer(row: RegistrationCustomSessionDataModelView, memberOptions: FieldOption[]): string {
  const value = formatValue(row);
  if (row.field?.fieldTypeCode !== 'member' || value === '—') return value;
  return memberOptions.find((o) => o.value === value)?.label ?? `Member #${value}`;
}

function isImageAnswer(row: RegistrationCustomSessionDataModelView): boolean {
  const value = formatValue(row);
  if (!value || value === '—') return false;

  const lowerValue = value.toLowerCase();
  const isUrl = lowerValue.startsWith('http://') || lowerValue.startsWith('https://');
  if (!isUrl) return false;

  if (row.field?.fieldTypeCode === 'url') return true;

  try {
    const decodedValue = decodeURIComponent(lowerValue);
    const hasImageExtension = /\.(jpeg|jpg|gif|png|webp|svg|bmp)/i.test(decodedValue);
    if (hasImageExtension) return true;
  } catch (e) {
    const hasImageExtension = /\.(jpeg|jpg|gif|png|webp|svg|bmp)/i.test(lowerValue);
    if (hasImageExtension) return true;
  }

  if (lowerValue.includes('s3.af-south-1.amazonaws.com/temp/uploads')) {
    return true;
  }

  return false;
}

function fieldFromRow(row: RegistrationCustomSessionDataModelView): BuilderField | null {
  const f = row.field;
  if (!f) return null;
  return {
    id: f.sessionCustomFieldID,
    fieldKey: f.fieldKey ?? '',
    label: f.label ?? '',
    fieldType: (f.fieldTypeCode ?? 'text') as BuilderField['fieldType'],
    category: f.category ?? 'General',
    orderNo: f.orderNo,
    required: f.isRequired,
    active: f.isActive,
    placeholder: f.placeholder ?? undefined,
    options: [], 
  };
}

export function SpecialSessionAnswersTab({ 
  registrationSessionID,
  onAnswersCleared,
  memberOptions = [],
}: SpecialSessionAnswersTabProps) {
  const registrationService = useRegistrationService();

  const [rows, setRows] = useState<RegistrationCustomSessionDataModelView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editRow, setEditRow] = useState<RegistrationCustomSessionDataModelView | null>(null);
  const [editValue, setEditValue] = useState<any>('');
  const [saving, setSaving] = useState(false);

  // ── Multi-select state ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ── Image viewer state ──────────────────────────────────────────────────
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerRow, setViewerRow] = useState<RegistrationCustomSessionDataModelView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await registrationService.getCustomSessionData(registrationSessionID);
      setRows(data);
      setSelectedIds([]); // reset selection
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load answers.');
    } finally {
      setLoading(false);
    }
  }, [registrationService, registrationSessionID]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (row: RegistrationCustomSessionDataModelView) => {
    setEditRow(row);
    setEditValue(row.valueBoolean ?? row.valueNumber ?? row.valueDecimal ?? row.valueDateTime ?? row.valueText ?? row.value ?? '');
  };

  const openViewer = (row: RegistrationCustomSessionDataModelView) => {
    setViewerRow(row);
    setViewerOpen(true);
  };

  const handleUpdate = async () => {
    if (!editRow) return;
    setSaving(true);
    setError(null);
    try {
      const value = typeof editValue === 'object' ? JSON.stringify(editValue) : String(editValue);
      await registrationService.updateCustomSessionData(editRow.registrationCustomSessionDataID, { value });
      setEditRow(null);
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to update answer.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: RegistrationCustomSessionDataModelView) => {
    setError(null);
    try {
      await registrationService.deleteCustomSessionData(row.registrationCustomSessionDataID);
      await load();
      onAnswersCleared?.(); // notify that we cleared answers
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete answer.');
    }
  };

  // Bulk clearing selected items
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    setError(null);
    try {
      await Promise.all(
        selectedIds.map((id) => registrationService.deleteCustomSessionData(id))
      );
      await load();
      onAnswersCleared?.(); // notify that answers are cleared
    } catch (e: any) {
      setError(e?.message ?? 'Failed bulk delete execution.');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelectRow = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === rows.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rows.map((r) => r.registrationCustomSessionDataID));
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const editField = editRow ? fieldFromRow(editRow) : null;

  const viewerFiles = viewerRow
    ? [
        {
          id: String(viewerRow.registrationCustomSessionDataID),
          url: formatValue(viewerRow),
          fileName: viewerRow.field?.label ?? 'Image',
          mimeType: 'image/*',
          fileSize: 0,
        },
      ]
    : [];

  return (
    <Stack gap="md">
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {rows.length > 0 && (
        <Card withBorder py="xs" px="sm" radius="md">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {selectedIds.length} of {rows.length} row(s) selected
            </Text>
            <Group gap="xs">
              <Button 
                color="red" 
                variant="light" 
                size="xs"
                leftSection={<IconTrash size={14} />}
                disabled={selectedIds.length === 0}
                loading={bulkDeleting}
                onClick={handleBulkDelete}
              >
                Clear Selected Answers
              </Button>
            </Group>
          </Group>
        </Card>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={IconClipboardOff}
          title="No answers captured yet"
          description="Once someone fills out this session's form, their answers will show up here."
          compact
        />
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 40 }}>
                <Checkbox 
                  checked={selectedIds.length === rows.length && rows.length > 0} 
                  indeterminate={selectedIds.length > 0 && selectedIds.length < rows.length}
                  onChange={toggleSelectAll} 
                />
              </Table.Th>
              <Table.Th>Question</Table.Th>
              <Table.Th>Answer</Table.Th>
              <Table.Th>Participant</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.registrationCustomSessionDataID}>
                <Table.Td>
                  <Checkbox 
                    checked={selectedIds.includes(row.registrationCustomSessionDataID)} 
                    onChange={() => toggleSelectRow(row.registrationCustomSessionDataID)} 
                  />
                </Table.Td>
                <Table.Td>{row.field?.label ?? '—'}</Table.Td>
                <Table.Td>
                  {isImageAnswer(row) ? (
                    <Group
                      gap={4}
                      style={{ cursor: 'pointer', display: 'inline-flex' }}
                      onClick={() => openViewer(row)}
                    >
                      <IconPhoto size={14} color="var(--mantine-color-blue-filled)" />
                      <Text size="sm" c="blue" td="underline">
                        View image
                      </Text>
                    </Group>
                  ) : (
                    formatAnswer(row, memberOptions)
                  )}
                </Table.Td>
                <Table.Td>{row.memberName ?? '—'}</Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end">
                    <ActionIcon variant="subtle" size="sm" onClick={() => openEdit(row)}>
                      <IconEdit size={14} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleDelete(row)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={!!editRow} onClose={() => setEditRow(null)} title="Edit Answer" size="md">
        {editField ? (
          <Stack gap="md">
            <DynamicField field={editField} value={editValue} onChange={setEditValue} memberOptions={memberOptions} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditRow(null)}>Cancel</Button>
              <Button loading={saving} onClick={handleUpdate}>Save</Button>
            </Group>
          </Stack>
        ) : (
          editRow && (
            <Text c="dimmed" size="sm">
              This question's definition is missing, so it can't be edited here.
            </Text>
          )
        )}
      </Modal>

      <UniversalFileViewer
        opened={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setViewerRow(null);
        }}
        files={viewerFiles}
        initialIndex={0}
        showGallery={false}
      />
    </Stack>
  );
}

export default SpecialSessionAnswersTab;